import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { MISSIONS, type Mission, levelForPoints } from "@/data/missions";

type ServerProgress = {
  userId: string;
  missionId: string;
  status: "available" | "active" | "completed";
  attempts: number;
  pointsAwarded: number;
  completedAt: string | null;
};

type MissionsResponse = {
  progress: ServerProgress[];
  totalPoints: number;
  level: number;
};

const ACTIVE_KEY = "marketlink.missions.active";
const ACTIVE_CHANGE_EVENT = "marketlink:mission-active-change";

export function useMissions() {
  const queryClient = useQueryClient();

  const { data } = useQuery<MissionsResponse>({
    queryKey: ["/api/my/missions"],
  });

  const [activeId, setActiveId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(ACTIVE_KEY);
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    function handler(ev: Event) {
      const detail = (ev as CustomEvent).detail as { id: string | null } | undefined;
      if (!detail) return;
      setActiveId(detail.id);
    }
    window.addEventListener(ACTIVE_CHANGE_EVENT, handler as EventListener);
    return () =>
      window.removeEventListener(ACTIVE_CHANGE_EVENT, handler as EventListener);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (activeId) window.localStorage.setItem(ACTIVE_KEY, activeId);
    else window.localStorage.removeItem(ACTIVE_KEY);
  }, [activeId]);

  const start = useMutation({
    mutationFn: async (missionId: string) => {
      const res = await apiRequest("POST", `/api/my/missions/${missionId}/start`);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/my/missions"] });
    },
  });

  const complete = useMutation({
    mutationFn: async ({ missionId, points }: { missionId: string; points: number }) => {
      const res = await apiRequest("POST", `/api/my/missions/${missionId}/complete`, {
        points,
      });
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/my/missions"] });
    },
  });

  const progressMap: Record<string, ServerProgress> = {};
  for (const p of data?.progress ?? []) progressMap[p.missionId] = p;

  return {
    missions: MISSIONS,
    progressMap,
    totalPoints: data?.totalPoints ?? 0,
    level: data ? data.level : levelForPoints(0),
    activeId,
    setActive: (id: string | null) => {
      setActiveId(id);
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent(ACTIVE_CHANGE_EVENT, { detail: { id } }),
        );
      }
      if (id) start.mutate(id);
    },
    completeMission: (mission: Mission) =>
      complete.mutate({ missionId: mission.id, points: mission.points }),
  };
}
